import { PlainButton } from "@jugl-web/ui-components";
import { ContactPackField } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import React, { useState } from "react";
import { FieldRow } from "../FieldRow";
import { createEmptyField } from "../../utils";
import { ReactComponent as PlusIcon } from "../../assets/plus.svg";

const MAX_CUSTOM_FIELDS = 7;

export interface SetFieldsTabProps {
  fields: ContactPackField[];
  fieldIds: string[];
  setFields: React.Dispatch<React.SetStateAction<ContactPackField[]>>;
  setFieldIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export const SetFieldsTab: React.FC<SetFieldsTabProps> = ({
  fields,
  fieldIds,
  setFields,
  setFieldIds,
}) => {
  const { t } = useTranslations();

  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  const updateField = (index: number) => (patch: Partial<ContactPackField>) => {
    setFields((current) =>
      current.map((field, i) => (i === index ? { ...field, ...patch } : field))
    );
  };

  const removeField = (index: number) => () => {
    setFields((current) => current.filter((_, i) => i !== index));
    setFieldIds((current) => current.filter((_, i) => i !== index));
  };

  const addField = () => {
    const newFieldId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

    setFocusFieldId(newFieldId);
    setFields((current) => [...current, createEmptyField()]);
    setFieldIds((current) => [...current, newFieldId]);
  };

  const defaultRows: { id: string; field: ContactPackField }[] = [
    {
      id: "default-name",
      field: {
        title: t({
          id: "cx-ai-agents-page.collect-leads-default-field-name",
          defaultMessage: "Name",
        }),
        type: "text",
        required: true,
        variable_name: "name",
      },
    },
    {
      id: "default-email",
      field: {
        title: t({
          id: "cx-ai-agents-page.collect-leads-default-field-email",
          defaultMessage: "Email",
        }),
        type: "email",
        required: true,
        variable_name: "email",
      },
    },
    {
      id: "default-phone",
      field: {
        title: t({
          id: "cx-ai-agents-page.collect-leads-default-field-phone",
          defaultMessage: "Phone",
        }),
        type: "phone",
        required: true,
        variable_name: "phone",
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h3 className="m-0 font-secondary text-base leading-[140%] font-semibold text-dark-900">
          {t({
            id: "cx-ai-agents-page.collect-leads-set-form-fields-title",
            defaultMessage: "Set Form Fields",
          })}
        </h3>
        <p className="m-0 font-secondary text-sm leading-[140%] text-dark-700">
          {t({
            id: "cx-ai-agents-page.collect-leads-set-form-fields-subtitle",
            defaultMessage: "Indicate which fields to ask your Customers",
          })}
        </p>
      </header>
      <div className="flex flex-col gap-5">
        {defaultRows.map((row) => (
          <FieldRow key={row.id} field={row.field} isLocked />
        ))}
        {fields.map((field, index) => (
          <FieldRow
            key={fieldIds[index]}
            field={field}
            onChange={updateField(index)}
            onRemove={removeField(index)}
            shouldFocusInput={fieldIds[index] === focusFieldId}
            onFocusApplied={() => setFocusFieldId(null)}
          />
        ))}
      </div>
      {fields.length < MAX_CUSTOM_FIELDS - defaultRows.length && (
        <div className="flex justify-start">
          <PlainButton
            onClick={addField}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-solid border-grey-300 bg-white px-4 font-secondary text-sm leading-[140%] font-medium text-dark-900 capitalize transition-colors hover:border-grey-600"
          >
            {t({
              id: "cx-ai-agents-page.collect-leads-add-field",
              defaultMessage: "Add field",
            })}
            <PlusIcon />
          </PlainButton>
        </div>
      )}
    </div>
  );
};
